import { Sprite } from "pixi.js"
import { Coordinate, TileSize, WindLeavesConfig } from "game-common/models"
import { grassKind } from "game-common/map/map"
import { GRASS_SPROUT_LEAN, LONG_GRASS_LEAN, SWAY_LEAN_BY_TEXTURE_ID } from "game-common/room/flora"
import { meadowSceneryLean } from "game-common/room/ember/meadow_grass"
import { daylightFraction } from "./day_gate"
import { windAt } from "./wind"

/**
 * The floor a blade grows on. A house's storeys share one wind, and each map clears and re-offers only
 * its own floor's blades, so climbing the stairs leaves the ground's grass in the wind.
 *
 * `drop` is how far down the screen the floor is drawn from the viewer's own storey, in world px: the
 * ground seen from upstairs sits a storey's rise lower, so its near set is gathered that much higher.
 */
export interface GrassFloor {
    drop(): number
}

// The viewer's own storey: where a blade registered without a floor grows.
const VIEWER_FLOOR: GrassFloor = { drop: () => 0 }

// One blade of grass under the wind. Registered when the map renders its cell and forgotten when that
// sprite is destroyed; everything here is a plain number so a frame costs no allocation.
interface Blade {
    sprite: Sprite
    floor: GrassFloor
    // Where the cell put the sprite. The sway writes sprite.x every frame, so the sprite is no longer
    // the record of where it belongs -- this is.
    baseX: number
    // Cell centre in world px, for the travelling-wave phase and the viewport test.
    worldX: number
    worldY: number
    // How far this piece's TOP edge leans at a full gust, in world px, and how far its BOTTOM edge
    // does. A plant is a stack of tiles, so a piece's bottom has to land exactly where the piece below
    // it put its top -- shear each one about its own base independently and the stack comes apart at
    // the joins, which is what it did first.
    leanTop: number
    leanBottom: number
    // A small per-cell offset so the wave has texture instead of arriving as a perfect front.
    jitter: number
}

/**
 * Grass bending in the wind.
 *
 * WHAT IT ACTUALLY DOES TO A SPRITE. Long grass rides the entity-tile surface as an individually owned
 * Sprite (MapRenderingManager.renderSpritedCell), so it can be transformed. Bending means the roots
 * stay planted and the tips lean, which is a SHEAR -- pixi's skew.x, which tilts the vertical axis.
 *
 * But skew shears about the sprite's origin, and these sprites are anchored at their TOP-left, so a
 * plain skew pins the top and swings the base: exactly backwards. The obvious fix is to move the pivot
 * down to the base, and it is a trap. Three other systems read these sprites' `y` as the top of the
 * cell -- the occluder fade that lets you see yourself behind tall grass (entity_tile_occluder_fade),
 * the tile-effect system's sprite ordering, and the damage overlay's placement -- and moving the pivot
 * means moving `y` by a whole tile, which silently puts the fade test a tile below the grass.
 *
 * So the shear stays about the top and the sprite is slid back by the amount its base moved. The base
 * lands exactly where it started, the tip carries the whole lean, and the only thing perturbed is `x`,
 * by at most the lean itself -- about four pixels, against a thirty-two pixel box. That is the entire
 * reason this is two writes a frame rather than one.
 *
 * WHY IT READS AS A GUST rather than a wobble. Every blade reads the SAME gust envelope as the
 * windblown leaves (wind.ts), so the two surge together, and a blade's phase is its position projected
 * onto the wind heading. That makes the crest travel ALONG the wind, so gusts visibly roll across the
 * clearing. Give every blade the same phase and the field twitches in unison; give each a random one
 * and it is just noise. The travelling wave is the whole effect.
 *
 * WHAT IT COSTS. The sprited layer is built for the whole room, not just what is on screen -- a forest
 * room holds hundreds of grass cells -- so the per-frame work is culled to the viewport and only the
 * blades actually in view pay for a sine and two writes. Destroyed sprites are pruned lazily here
 * rather than by a hook in the map's removal paths, so a cell that disappears needs to tell this
 * nothing.
 *
 * LIVE TUNING, from the browser console:
 *   window.grassSway = false        stop the grass dead (the leaves keep blowing)
 *   window.grassSwayLean = 8        multiply every blade's lean
 *   window.grassSwaySpeed = 1       multiply the wave's speed
 */
export class GrassSway {
    // How fast a blade oscillates, in radians/sec, before the console multiplier.
    private static readonly FREQUENCY = 1.9
    // How far apart the crests of the travelling wave are, in world px. About six tiles: short enough
    // that a gust is visibly moving across the view, long enough that neighbours lean together.
    private static readonly WAVELENGTH = 6 * TileSize
    // How much a blade's own phase is scattered off the wave front, in radians.
    private static readonly JITTER = 0.9
    // The bend is a horizontal shear, so only the wind's horizontal component can show as a lean -- a
    // wind blowing straight up or down the screen bends grass towards the viewer, which a flat sprite
    // cannot draw. Rooms with a cross-screen wind (ember's is 22 degrees) get the full effect.
    //
    // The hinge height: the blade pivots about the bottom of its cell.
    private static readonly HINGE = TileSize
    // Beyond the viewport by this much is still updated, so a blade is already mid-lean when it scrolls
    // in rather than snapping upright to bent on its first visible frame.
    private static readonly MARGIN = 2 * TileSize
    // The wind calms after dark but never stops: the leaves settle for the night (day_gate.ts) and the
    // grass keeps a little stir, so the world does not freeze solid the moment the fog comes up.
    private static readonly NIGHT_CALM = 0.3
    // Where the bend profile starts, as a share of the full lean, at the top of the piece sitting ON
    // the anchor row. Not zero: grass rooted in the ground still stirs low down, and a base held
    // perfectly rigid under a leaning top reads as a hinge rather than as a plant.
    private static readonly ROOT_SHARE = 0.18
    // How far the player may move before the near set is gathered again. Half the margin, so a blade
    // cannot cross the whole margin and reach the screen before it is picked up.
    private static readonly REGATHER = TileSize

    private blades: Blade[] = []
    private running = false
    // The blades near enough to matter, rebuilt only when the player has actually moved somewhere new.
    // A room holds well over a thousand blades and fewer than a twentieth are ever on screen; testing
    // every one of them every frame measured at about 0.8ms a frame on a fast machine, which is real
    // money on the low-end target for an effect nobody is looking directly at.
    private visible: Blade[] = []
    private visibleAtX = 0
    private visibleAtY = 0
    private visibleValid = false

    /**
     * Take an interest in a sprited cell, if it is something that bends. Called for every sprited cell
     * the map renders, so it has to be cheap and it has to say no quickly.
     */
    register = (sprite: Sprite, cellX: number, cellY: number, textureId: string, heightAbove = 0, entityTileId?: string,
        floor: GrassFloor = VIEWER_FLOOR) => {
        // Known LPC art by its texture; a meadow clump or sprout by its `grass` tag, and the meadow's
        // scenery (tufts, dirt sedges: no tag to carry) by the texture its catalog cell resolves to --
        // Lab art lands in whatever authored-terrain cell was free, so no texture id is written down.
        const kind = grassKind(entityTileId)
        const lean = SWAY_LEAN_BY_TEXTURE_ID[textureId]
            ?? (kind === "long" ? LONG_GRASS_LEAN : kind === "sprout" ? GRASS_SPROUT_LEAN : meadowSceneryLean(textureId))
        if (lean === undefined) {
            return
        }
        // Where this piece's two edges sit on the plant's bend profile. `heightAbove` is the sub-tile's
        // own row above the anchor, so the profile is evaluated at the top of this piece and at the top
        // of the piece below it -- which is this one's bottom. Cumulative by construction: the joins
        // cannot come apart, however hard the wind blows.
        //
        // The ground itself never moves, so the base piece's bottom is pinned at zero.
        const profile = (h: number) => (lean * (h + GrassSway.ROOT_SHARE)) / (1 + GrassSway.ROOT_SHARE)
        const leanTop = profile(heightAbove)
        const leanBottom = heightAbove === 0 ? 0 : profile(heightAbove - 1)
        const worldX = cellX * TileSize
        const worldY = cellY * TileSize
        this.blades.push({
            sprite,
            floor,
            baseX: sprite.x,
            worldX,
            worldY,
            leanTop,
            leanBottom,
            // A cheap positional hash: no per-blade storage, stable across a rebuild of the same room,
            // and uncorrelated enough between neighbours to break up the wave front.
            jitter: ((Math.sin(cellX * 12.9898 + cellY * 78.233) * 43758.5453) % 1) * GrassSway.JITTER,
        })
        this.visibleValid = false
    }

    // The map tore down every cell of a floor (every floor, given none) for a full rebuild; the blades
    // went with them. This matters more here than it looks: these sprites come from a POOL, so a stale
    // reference is not merely dead, it is a live sprite that now belongs to some other tile -- which is
    // why the near set loses them too, or the next gather would straighten someone else's sprite.
    clear = (floor?: GrassFloor) => {
        if (floor) {
            this.blades = this.blades.filter(blade => blade.floor !== floor)
            this.visible = this.visible.filter(blade => blade.floor !== floor)
            this.visibleValid = false
            return
        }
        this.blades = []
        this.visible = []
        this.visibleValid = false
        this.running = false
    }

    // Stand a floor's blades (every floor's, given none) upright, then forget them. Their sprites live on
    // and are not recycled, so one left mid-lean would stay bent.
    detachAll = (floor?: GrassFloor) => {
        if (!floor) {
            this.release()
            this.clear()
            return
        }
        for (const blade of this.blades) {
            if (blade.floor === floor && !blade.sprite.destroyed) {
                blade.sprite.skew.x = 0
                blade.sprite.x = blade.baseX
            }
        }
        this.clear(floor)
    }

    // Exposed for tests: how many blades are registered (on one floor, given one), and how many moved on
    // the last frame.
    getBladeCount = (floor?: GrassFloor) => floor ? this.blades.filter(blade => blade.floor === floor).length : this.blades.length
    swayingCount = 0

    /**
     * A blade of grass near (x, y) that something could land on, or null if there is none within
     * `radius`. The butterflies perch on these (see Butterflies) -- which is why this reads the FULL
     * registry rather than the near set: the near set only exists while the wind is blowing, and a
     * still room still has grass to sit on.
     *
     * The point returned is the top of the plant, not the cell it is rooted in. Long grass is two tiles
     * tall, and a butterfly sitting at its base would be down in the roots with the blades drawn over
     * the top of it.
     *
     * Only the viewer's own storey: the butterflies fly in its surface, so one perched on the ground
     * seen from upstairs would be drawn over the upper floor.
     *
     * Reservoir sampling so it is an even pick among the candidates without building a list -- this
     * walks the room's blades, and at a few calls a second that is cheap, but allocating an array of
     * them each time would not be.
     */
    perchNear = (x: number, y: number, radius: number): { x: number; y: number } | null => {
        let chosen: Blade | undefined
        let seen = 0
        for (const blade of this.blades) {
            if (blade.sprite.destroyed || blade.floor.drop() !== 0) {
                continue
            }
            if (Math.abs(blade.worldX - x) > radius || Math.abs(blade.worldY - y) > radius) {
                continue
            }
            seen++
            if (Math.random() * seen < 1) {
                chosen = blade
            }
        }
        if (!chosen) {
            return null
        }
        // Middle of the cell, and up near the tips rather than the roots -- up a hill as the clump is drawn.
        const lift = (chosen.sprite as Sprite & { hillLift?: number }).hillLift ?? 0
        return { x: chosen.worldX + TileSize / 2, y: chosen.worldY - TileSize * 0.35 - lift }
    }

    /**
     * Bend the grass for this frame.
     *
     * @param config the room's wind (see wind.ts -- the leaves' config IS the room's wind)
     * @param center the player's world position; the box follows them
     * @param brightness the client's interpolated daylight, or undefined in a room with no day cycle
     */
    update = (config: WindLeavesConfig | null, center?: Coordinate, brightness?: number) => {
        const w = window as any
        const enabled = w.grassSway !== false
        const wind = enabled && center ? windAt(config, performance.now() / 1000) : null

        if (!wind) {
            // Stand everything upright once, then stop paying anything until the wind returns. Without
            // this a room whose wind is switched off keeps its grass frozen mid-lean.
            if (this.running) {
                this.release()
            }
            return
        }
        this.running = true

        const leanScale = typeof w.grassSwayLean === "number" ? w.grassSwayLean : 1
        const speedScale = typeof w.grassSwaySpeed === "number" ? w.grassSwaySpeed : 1
        // Calmer after dark, in step with the leaves settling, but never quite still.
        const hour = GrassSway.NIGHT_CALM + (1 - GrassSway.NIGHT_CALM) * daylightFraction(brightness)
        const amplitude = wind.gust * wind.x * leanScale * hour

        const tSeconds = (performance.now() / 1000) * GrassSway.FREQUENCY * speedScale
        const waveNumber = (Math.PI * 2) / GrassSway.WAVELENGTH
        const halfW = (7 + 2) * TileSize + GrassSway.MARGIN
        const halfH = (5 + 2) * TileSize + GrassSway.MARGIN

        // Re-gather the near set only when the player has left the box it was gathered for. Walking a
        // whole room's blades every frame is the one thing about this effect that could actually cost
        // something; walking them once every few tiles costs nothing.
        if (
            !this.visibleValid ||
            Math.abs(center!.x - this.visibleAtX) > GrassSway.REGATHER ||
            Math.abs(center!.y - this.visibleAtY) > GrassSway.REGATHER
        ) {
            this.gather(center!.x, center!.y, halfW, halfH)
        }

        let swaying = 0
        for (let i = 0; i < this.visible.length; i++) {
            const blade = this.visible[i]
            const sprite = blade.sprite
            if (sprite.destroyed) {
                // A pooled sprite that has been handed to something else. Drop the whole near set and
                // rebuild it next frame rather than reasoning about which half is still good.
                this.visibleValid = false
                continue
            }

            const phase = (blade.worldX * wind.x + blade.worldY * wind.y) * waveNumber + blade.jitter
            // Signed tip displacement in world px. The minus on the shear is what makes a positive lean
            // carry the TIP downwind: skew about the top moves the base, and sliding the sprite back by
            // the same amount lands the base where it started and hands the whole displacement to the
            // tip.
            const wave = amplitude * Math.sin(tSeconds - phase)
            const top = blade.leanTop * wave
            const bottom = blade.leanBottom * wave
            // The shear only has to supply the DIFFERENCE across this piece; the translation puts its
            // bottom where the piece below it left off. Together they hand the top edge `top` and the
            // bottom edge `bottom`, so neighbouring pieces meet EXACTLY.
            //
            // asin, not the small-angle shortcut. The error in `skew ~= offset / HINGE` is under a
            // hundredth of a world pixel, which sounds ignorable and is not: the world surface draws at
            // 2x, and a fraction of a pixel of disagreement between two halves of the same plant shows
            // up as a visible seam along a hard pixel-art edge. One asin per visible blade per frame is
            // cheaper than a seam.
            sprite.skew.x = Math.asin(Math.max(-1, Math.min(1, (bottom - top) / GrassSway.HINGE)))
            sprite.x = blade.baseX + top
            swaying++
        }

        this.swayingCount = swaying
    }

    /**
     * Collect the blades near enough to be worth moving, and straighten the ones leaving the set --
     * otherwise a blade scrolls off frozen mid-lean and is still bent when it comes back.
     *
     * This is also where destroyed sprites are pruned. Per-cell removals in the map have no hook into
     * the wind, by design: a cell that disappears should not have to know this exists.
     */
    private gather = (centerX: number, centerY: number, halfW: number, halfH: number) => {
        for (const blade of this.visible) {
            if (!blade.sprite.destroyed) {
                blade.sprite.skew.x = 0
                blade.sprite.x = blade.baseX
            }
        }

        const near: Blade[] = []
        const alive: Blade[] = []
        let anyDead = false
        for (const blade of this.blades) {
            if (blade.sprite.destroyed) {
                anyDead = true
                continue
            }
            alive.push(blade)
            if (Math.abs(blade.worldX - centerX) <= halfW && Math.abs(blade.worldY + blade.floor.drop() - centerY) <= halfH) {
                near.push(blade)
            }
        }
        if (anyDead) {
            this.blades = alive
        }
        this.visible = near
        this.visibleAtX = centerX
        this.visibleAtY = centerY
        this.visibleValid = true
    }

    // Put every blade back upright and forget the destroyed ones.
    private release = () => {
        this.running = false
        this.swayingCount = 0
        this.visible = []
        this.visibleValid = false
        const alive: Blade[] = []
        for (const blade of this.blades) {
            if (blade.sprite.destroyed) {
                continue
            }
            blade.sprite.skew.x = 0
            blade.sprite.x = blade.baseX
            alive.push(blade)
        }
        this.blades = alive
    }
}
